import React from 'react'
import URL from 'src/api/url'
import Axios from 'axios'
import { ToolbarView, BalloonPanelView, ButtonView, View, LabeledFieldView, createLabeledInputText, submitHandler, FocusTracker, KeystrokeHandler, FocusCycler, ViewCollection, icons } from 'ckeditor5'
import ContainerMediaGalery from 'src/container/image-gallery'
import SelectedIcon from 'src/components/selected-icon'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'

class TextAlternativeFormView extends View {
  constructor(locale) {
    super(locale)
    const t = this.locale.t
    this.focusTracker = new FocusTracker()
    this.keystrokes = new KeystrokeHandler()
    this.labeledInput = this._createLabeledInputView()
    this.saveButtonView = this._createButton(t('Save'), icons.check, 'ck-button-save')
    this.saveButtonView.type = 'submit'
    this.cancelButtonView = this._createButton(t('Cancel'), icons.cancel, 'ck-button-cancel', 'cancel')
    this._focusables = new ViewCollection()
    this._focusCycler = new FocusCycler({
      focusables: this._focusables,
      focusTracker: this.focusTracker,
      keystrokeHandler: this.keystrokes,
      actions: {
        focusPrevious: 'shift + tab',
        focusNext: 'tab',
      },
    })
    this.setTemplate({
      tag: 'form',
      attributes: {
        class: ['ck', 'ck-text-alternative-form', 'ck-responsive-form'],
        tabindex: '-1',
      },
      children: [this.labeledInput, this.saveButtonView, this.cancelButtonView],
    })
  }

  render() {
    super.render()
    this.keystrokes.listenTo(this.element)
    submitHandler({ view: this })
      ;[this.labeledInput, this.saveButtonView, this.cancelButtonView].forEach(v => {
        this._focusables.add(v)
        this.focusTracker.add(v.element)
      })
  }

  destroy() {
    super.destroy()
    this.focusTracker.destroy()
    this.keystrokes.destroy()
  }

  _createButton(label, icon, className, eventName) {
    const button = new ButtonView(this.locale)
    button.set({ label, icon, tooltip: true })
    button.extendTemplate({ attributes: { class: className } })
    if (eventName) {
      button.delegate('execute').to(this, eventName)
    }
    return button
  }

  _createLabeledInputView() {
    const t = this.locale.t
    const labeledInput = new LabeledFieldView(this.locale, createLabeledInputText)
    labeledInput.label = t('Text alternative')
    return labeledInput
  }
}

const defaultBadgePreview = `<div style="position:relative;width:120px;display:flex;justify-content:center;align-items:center;">
  <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="path-1-inside-1_badge_preview" fill="white">
      <path d="M0.125343 17.9801C-1.06545 8.43188 6.38019 0 16.0024 0H103.998C113.583 0 121.017 8.36941 119.886 17.8871L111.084 91.9999C110.391 97.8305 106.561 102.813 101.104 104.982L66.1568 118.869C62.3621 120.377 58.1344 120.377 54.3397 118.869L19.3338 104.958C13.9083 102.802 10.0878 97.8626 9.36532 92.0694L0.125343 17.9801Z"/>
    </mask>
    <path d="M0.125343 17.9801C-1.06545 8.43188 6.38019 0 16.0024 0H103.998C113.583 0 121.017 8.36941 119.886 17.8871L111.084 91.9999C110.391 97.8305 106.561 102.813 101.104 104.982L66.1568 118.869C62.3621 120.377 58.1344 120.377 54.3397 118.869L19.3338 104.958C13.9083 102.802 10.0878 97.8626 9.36532 92.0694L0.125343 17.9801Z" fill="white"/>
    <path d="M66.1568 118.869L65.7875 117.939L66.1568 118.869ZM54.3397 118.869L53.9705 119.798L54.3397 118.869ZM9.36532 92.0694L8.37301 92.1932L9.36532 92.0694ZM19.3338 104.958L19.7031 104.029L19.3338 104.958ZM111.084 91.9999L110.091 91.8819L111.084 91.9999ZM101.104 104.982L101.473 105.911L101.104 104.982ZM119.886 17.8871L120.879 18.0051L119.886 17.8871ZM0.125343 17.9801L1.11766 17.8563L0.125343 17.9801ZM16.0024 1H103.998V-1H16.0024V1ZM118.893 17.7692L110.091 91.8819L112.077 92.1178L120.879 18.0051L118.893 17.7692ZM10.3576 91.9457L1.11766 17.8563L-0.86697 18.1038L8.37301 92.1932L10.3576 91.9457ZM100.735 104.052L65.7875 117.939L66.526 119.798L101.473 105.911L100.735 104.052ZM54.709 117.939L19.7031 104.029L18.9645 105.888L53.9705 119.798L54.709 117.939ZM65.7875 117.939C62.23 119.353 58.2665 119.353 54.709 117.939L53.9705 119.798C58.0023 121.4 62.4942 121.4 66.526 119.798L65.7875 117.939ZM8.37301 92.1932C9.14066 98.3485 13.2 103.597 18.9645 105.888L19.7031 104.029C14.6167 102.008 11.035 97.3768 10.3576 91.9457L8.37301 92.1932ZM110.091 91.8819C109.441 97.3482 105.85 102.02 100.735 104.052L101.473 105.911C107.271 103.607 111.341 98.3129 112.077 92.1178L110.091 91.8819ZM103.998 1C112.984 1 119.953 8.84633 118.893 17.7692L120.879 18.0051C122.081 7.8925 114.182 -1 103.998 -1V1ZM16.0024 -1C5.77881 -1 -2.13219 7.95888 -0.86697 18.1038L1.11766 17.8563C0.00128733 8.90489 6.98157 1 16.0024 1V-1Z" fill="#015FFB" mask="url(#path-1-inside-1_badge_preview)"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.29913 77.6247C0.775323 76.9699 1.2415 76 2.08 76H19.9994H99.9994H117.919C118.757 76 119.223 76.9699 118.7 77.6247L112.499 85.3753C112.207 85.7405 112.207 86.2595 112.499 86.6247L118.7 94.3753C119.223 95.0301 118.757 96 117.919 96H99.9994H19.9994H2.08C1.2415 96 0.775323 95.0301 1.29913 94.3753L7.49962 86.6247C7.7918 86.2595 7.7918 85.7405 7.49962 85.3753L1.29913 77.6247Z" fill="#FAC101"/>
  </svg>
  <div style="width:120px;height:20px;left:0;top:75px;position:absolute;">
    <div style="display:flex;justify-content:center;align-items:center;height:20px;font-size:12px;font-family:Inter;">{ribbonText}</div>
  </div>
  <div style="height:24px;left:0;top:44px;position:absolute;justify-content:center;align-items:center;display:inline-flex;width:120px;">
    <div style="text-align:center;color:#002e47;font-size:12px;font-family:Inter;font-weight:500;word-wrap:break-word;">{title}</div>
  </div>
  <div style="width:60px;height:20px;left:30px;top:15px;position:absolute;display:flex;align-items:center;justify-content:center;">
    <img style="width:auto !important;height:20px !important" src="{logo}" />
  </div>
</div>`

let lastRequestId = null
let lastFetchTime = null
const CACHE_TTL = 30000 // 30 seconds

const fetchBadges = async collectionId => {
  try {
    const url = URL.API + URL.COLLECTION_BADGES.replace('{id}', collectionId)
    const response = await Axios.get(url)
    return response.data?.data || []
  } catch (error) {
    console.error('Badge fetch error:', error.response?.data || error.message)
    return []
  }
}

const fetchBadgeReward = async collectionId => {
  try {
    const url =
      URL.API + URL.COLLECTION_BADGE_REWARD.replace('{id}', collectionId)
    const response = await Axios.get(url)
    return response.data?.data || null
  } catch (error) {
    console.error(
      'Badge reward fetch error:',
      error.response?.data || error.message,
    )
    return null
  }
}

const fetchCourses = async collectionId => {
  try {
    const url =
      URL.API +
      URL.COLLECTION_COURSE_LIST.replace('{collectionId}', collectionId)
    const response = await Axios.get(url)
    return response.data?.data || []
  } catch (error) {
    console.error(
      'Courses fetch error:',
      error.response?.data || error.message,
    )
    return []
  }
}

const updateBadgeReward = async (collectionId, rewardHtml, isEnabled) => {
  try {
    const url =
      URL.API + URL.COLLECTION_BADGE_REWARD.replace('{id}', collectionId)
    await Axios.put(url, { rewardHtml, isEnabled })
  } catch (error) {
    console.error(
      'Badge reward update error:',
      error.response?.data || error.message,
    )
  }
}


const defaultRewardCardHtml = () => `
  <div class="badge-completion-reward-card">
    <div class="badge-reward-image-container">
      <div class="img-wrapper ck-editor__editable ck-editor__nested-editable" role="textbox" tabindex="-1" contenteditable="true">
        <span class="image-inline ck-widget" contenteditable="false"><img src="https://cdn.minicoursegenerator.com/assets/badge/badgeplaceholder.png" alt="" /></span>
      </div>
    </div>
    <div class="badge-reward-text-container">
      <div class="badge-reward-title" contenteditable="true" data-cke-ignore-events="true">&#127881; Congratulations!</div>
      <div class="badge-reward-description" contenteditable="true" data-cke-ignore-events="true">All the badges are collected and your earned the reward! Here's something special for you.</div>
    </div>
  </div>`

const renderCompletionReward = (rewardData, bgColor, collectionId, isPreview = false) => {
  const isEnabled = rewardData?.isEnabled !== false
  let cardHtml = rewardData?.rewardHtml || defaultRewardCardHtml()
  // Strip any existing style from card and text container, then re-apply bgColor
  cardHtml = cardHtml.replace(/(<div class="badge-completion-reward-card")\s*style="[^"]*"/, '$1')
  cardHtml = cardHtml.replace(/(<div class="badge-reward-text-container")\s*style="[^"]*"/, '$1')
  if (bgColor) {
    cardHtml = cardHtml.replace(
      '<div class="badge-completion-reward-card"',
      `<div class="badge-completion-reward-card" style="background: ${bgColor};"`,
    )
  }
  // Remove settings icon from saved HTML
  cardHtml = cardHtml.replace(/<div class="badge-reward-settings-icon">[\s\S]*?<\/svg>\s*<\/div>/, '')

  // In preview, only show the card (no header/toggle) and only if enabled
  if (isPreview) {
    if (!isEnabled) return ''
    // Remove contenteditable and cke attributes for preview
    let previewCardHtml = cardHtml
      .replace(/\s*contenteditable="[^"]*"/g, '')
      .replace(/\s*data-cke-ignore-events="[^"]*"/g, '')
    return `
      <div class="badge-completion-reward badge-completion-reward-preview" data-collection-id="${collectionId || ''}">
        ${previewCardHtml}
      </div>`
  }

  return `
    <div class="badge-completion-reward" data-collection-id="${collectionId || ''}">
      <div class="badge-completion-reward-header">
        <div class="badge-completion-reward-header-left">
          <span class="badge-reward-trophy"><iconify-icon icon="mage:trophy-fill" width="20" height="20" style="color:#F59E0B;vertical-align:middle;"></iconify-icon></span>
          <span class="badge-reward-header-title">Completion Reward</span>
          <span class="badge-reward-optional">Optional</span>
        </div>
        <div class="badge-completion-reward-header-right">
          <span class="badge-reward-toggle-label">Shown when all badges are completed</span>
          <label class="badge-reward-switch">
            <input type="checkbox" class="badge-reward-toggle" ${isEnabled ? 'checked' : ''} data-cke-ignore-events="true" />
            <span class="badge-reward-slider"></span>
          </label>
        </div>
      </div>
      <div class="badge-completion-reward-divider"></div>
      ${isEnabled ? cardHtml : `<p class="badge-reward-disabled-text">Provide a special reward (e.g., a discount code or exclusive access to a feature) once a user collects all badges added below.</p>`}
    </div>`
}

const openRewardImageGallery = (container, collectionId, type) => {
  const dispatch = window.store?.dispatch
  if (!dispatch) return

  const setImageURL = () => { }

  const onOk = item => {
    const itemUrl = item[0].url
    const imgContainer = container.querySelector(
      '.badge-reward-image-container',
    )
    if (imgContainer) {
      imgContainer.innerHTML = `<div class="img-wrapper ck-editor__editable ck-editor__nested-editable" role="textbox" tabindex="-1" contenteditable="true"><span class="image-inline ck-widget" contenteditable="false"><img src="${itemUrl}" alt=""/></span></div>`
    }

    const rewardCard = container.querySelector('.badge-completion-reward-card')
    const toggle = container.querySelector('.badge-reward-toggle')
    const isEnabled = toggle ? toggle.checked : true
    // Save without inline background - bgColor comes from simple-box-wrapper attribute
    if (rewardCard) {
      const cardClone = rewardCard.cloneNode(true)
      cardClone.style.background = ''
      updateBadgeReward(collectionId, cardClone.outerHTML, isEnabled)
      // Update cached reward data so reconversions keep the new image
      if (window.badgeRewardData) {
        window.badgeRewardData.rewardHtml = cardClone.outerHTML
      }
    }

    dispatch(messageBoxHideAction())
  }

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        buttons: [],
        title: `<div style="display:flex;align-items:center;justify-content:center;gap:8px;">Media Gallery</div>`,
        component: (
          <ContainerMediaGalery onOk={onOk} setImageURL={setImageURL} type={type} />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        maxHeight: '640px',
      }),
    ),
  )
}

const initRewardEvents = (container, collectionId, editor, rewardBgColor) => {
  if (!container || !collectionId) return

  const toggle = container.querySelector('.badge-reward-toggle')
  const getCleanCardHtml = () => {
    const rewardCard = container.querySelector('.badge-completion-reward-card')
    if (!rewardCard) return ''
    const cardClone = rewardCard.cloneNode(true)
    cardClone.style.background = ''
    return cardClone.outerHTML
  }

  const saveRewardContent = () => {
    const isEnabled = toggle ? toggle.checked : true
    const html = getCleanCardHtml()
    if (window.badgeRewardData) {
      window.badgeRewardData.isEnabled = isEnabled
      if (html) window.badgeRewardData.rewardHtml = html
    }
    updateBadgeReward(collectionId, html, isEnabled)
  }

  const reattachBlurEvents = () => {
    const newTitle = container.querySelector('.badge-reward-title')
    const newDesc = container.querySelector('.badge-reward-description')
    if (newTitle) newTitle.addEventListener('blur', saveRewardContent)
    if (newDesc) newDesc.addEventListener('blur', saveRewardContent)
  }

  let rewardToolbarView = null
  let skipNextOutsideClick = false

  const attachImageClickHandler = () => {
    const imgContainer = container.querySelector('.badge-reward-image-container')
    if (!imgContainer) return

    imgContainer.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()

      const contextualBalloon = editor.plugins.get('ContextualBalloon')

      // Toggle if already visible
      if (rewardToolbarView && contextualBalloon.hasView(rewardToolbarView)) {
        contextualBalloon.remove(rewardToolbarView)
        rewardToolbarView = null
        return
      }

      const toolbarView = new ToolbarView(editor.locale)
      toolbarView.isCompact = true

      const altBtn = new ButtonView(editor.locale)
      altBtn.set({
        label: 'Change image text alternative',
        icon: icons.textAlternative,
        tooltip: true,
      })
      altBtn.on('execute', () => {
        skipNextOutsideClick = true
        contextualBalloon.remove(toolbarView)
        rewardToolbarView = null

        const img = imgContainer.querySelector('img')
        const currentAlt = img ? img.getAttribute('alt') || '' : ''

        const altFormView = new TextAlternativeFormView(editor.locale)
        altFormView.render()

        const labeledInput = altFormView.labeledInput
        labeledInput.fieldView.value = labeledInput.fieldView.element.value = currentAlt

        altFormView.on('submit', () => {
          if (img) {
            img.setAttribute('alt', altFormView.labeledInput.fieldView.element.value)
          }
          contextualBalloon.remove(altFormView)
          rewardToolbarView = null
          saveRewardContent()
        })

        altFormView.on('cancel', () => {
          contextualBalloon.remove(altFormView)
          rewardToolbarView = null
        })

        altFormView.keystrokes.set('Esc', (data, cancel) => {
          contextualBalloon.remove(altFormView)
          rewardToolbarView = null
          cancel()
        })

        contextualBalloon.add({
          view: altFormView,
          position: {
            target: imgContainer,
            positions: [
              BalloonPanelView.defaultPositions.northArrowSouth,
              BalloonPanelView.defaultPositions.southArrowNorth,
            ],
          },
        })
        rewardToolbarView = altFormView

        setTimeout(() => altFormView.labeledInput.fieldView.select(), 50)
      })

      const aiBtn = editor.ui.componentFactory.create('imageGalleryAi')
      aiBtn.off('execute')
      aiBtn.on('execute', () => {
        contextualBalloon.remove(toolbarView)
        rewardToolbarView = null
        openRewardImageGallery(container, collectionId, 'ai')
      })

      const changeBtn = editor.ui.componentFactory.create('imageGallery')
      changeBtn.off('execute')
      changeBtn.on('execute', () => {
        contextualBalloon.remove(toolbarView)
        rewardToolbarView = null
        openRewardImageGallery(container, collectionId)
      })

      toolbarView.items.addMany([altBtn, aiBtn, changeBtn])
      toolbarView.render()

      contextualBalloon.add({
        view: toolbarView,
        position: {
          target: imgContainer,
          positions: [
            BalloonPanelView.defaultPositions.northArrowSouth,
            BalloonPanelView.defaultPositions.southArrowNorth,
          ],
        },
      })

      rewardToolbarView = toolbarView
    })
  }

  if (toggle) {
    toggle.addEventListener('change', () => {
      // Update cached reward data so re-renders keep the correct state
      if (window.badgeRewardData) {
        window.badgeRewardData.isEnabled = toggle.checked
      }

      const disabledText = container.querySelector('.badge-reward-disabled-text')
      const rewardCard = container.querySelector('.badge-completion-reward-card')

      if (toggle.checked) {
        // Switch from disabled text to card
        if (disabledText) {
          let savedHtml = window.badgeRewardData?.rewardHtml || defaultRewardCardHtml()
          // Re-apply bgColor
          if (rewardBgColor) {
            savedHtml = savedHtml.replace(
              '<div class="badge-completion-reward-card"',
              `<div class="badge-completion-reward-card" style="background: ${rewardBgColor};"`,
            )
          }
          disabledText.outerHTML = savedHtml
          // Re-attach events on newly inserted DOM elements
          reattachBlurEvents()
          attachImageClickHandler()
        }
        // Send the restored card HTML to API
        updateBadgeReward(collectionId, getCleanCardHtml(), true)
      } else {
        // Switch from card to disabled text
        if (rewardCard) {
          // Save current card HTML before hiding
          const cardClone = rewardCard.cloneNode(true)
          cardClone.style.background = ''
          const savedCardHtml = cardClone.outerHTML
          if (window.badgeRewardData) {
            window.badgeRewardData.rewardHtml = savedCardHtml
          }
          rewardCard.outerHTML = `<p class="badge-reward-disabled-text">Provide a special reward (e.g., a discount code or exclusive access to a feature) once a user collects all badges added below.</p>`
          // Send the saved card HTML (not getCleanCardHtml which returns '' now)
          updateBadgeReward(collectionId, savedCardHtml, false)
        }
      }
    })
  }

  // Initial event attachment
  reattachBlurEvents()
  attachImageClickHandler()

  // Close balloon when clicking outside (dynamic lookup for imgContainer)
  document.addEventListener('click', e => {
    if (skipNextOutsideClick) {
      skipNextOutsideClick = false
      return
    }
    if (rewardToolbarView) {
      const currentImgContainer = container.querySelector('.badge-reward-image-container')
      if (currentImgContainer && currentImgContainer.contains(e.target)) return
      const contextualBalloon = editor.plugins.get('ContextualBalloon')
      const balloonElement = contextualBalloon.view.element
      if (balloonElement && balloonElement.contains(e.target)) return
      if (contextualBalloon.hasView(rewardToolbarView)) {
        contextualBalloon.remove(rewardToolbarView)
        rewardToolbarView = null
      }
    }
  })
}

const placeholderLogoUrl =
  'https://cdn.minicoursegenerator.com/test/images/mehmetakif1636638891/a68920bd-189c-49d7-ae13-2a2027524aba.png'

const renderBadgePreview = badge => {
  if (badge.badgeType === 'custom' && badge.customBadgeImageUrl) {
    return `<img class="custom-badge-img" src="${badge.customBadgeImageUrl}" alt="${badge.badgeTitle || 'Custom Badge'}" />`
  }

  const title = badge.badgeTitle || 'Badge Title'
  const ribbonText = badge.badgeRibbonText || 'CERTIFIED'
  const logo = badge.badgeLogo || badge.logoUrl || placeholderLogoUrl

  let html = badge.badgeValue || defaultBadgePreview
  html = html.replace('{title}', title).replace('{ribbonText}', ribbonText)
  html = html.replaceAll('{logo}', logo)

  return html
}

const renderBadgeCard = (badge, startText = 'Start', isPreview = false) => {
  return `
    <div class="badge-list-card" data-badge-id="${badge.badgeId}" data-card-id="${badge.cardId}" data-course-id="${badge.courseId}">
      <div class="badge-list-card-image">
        ${renderBadgePreview(badge)}
      </div>
      <div class="badge-list-card-title">
        <p>${badge.badgeTitle || badge.courseName || 'Badge'}</p>
      </div>
      <div class="badge-list-card-start-btn">
        ${startText}
        <iconify-icon icon="mdi:chevron-right" width="20" height="20"></iconify-icon>
      </div>
      ${!isPreview ? `<div class="badge-list-card-edit-btn" data-course-id="${badge.courseId}">
        <iconify-icon icon="mdi:pencil" width="16" height="16"></iconify-icon>
      </div>` : ''}
    </div>`
}

const CreateBadgeList = async (collectionId, rewardBgColor, headerTitle, headerIcon, startText = 'Start', isPreview = false, resetReward = false) => {
  if (!collectionId) {
    return `
      <div class="badge-list-wrapper">
        <div class="badge-list-empty">No learning path found</div>
      </div>
    `
  }

  const requestId = `badges-${collectionId}`

  // Invalidate cache if new creation, badge settings changed, or cache is stale
  if (resetReward || window.badgeListNeedsRefresh || (lastFetchTime && Date.now() - lastFetchTime > CACHE_TTL)) {
    window.badgeListNeedsRefresh = false
    lastRequestId = null
    lastFetchTime = null
  }

  if (lastRequestId === requestId && window.badgesData && window.badgeCoursesData) {
    return buildBadgeListHtml(
      window.badgesData,
      window.badgeRewardData,
      rewardBgColor,
      collectionId,
      window.badgeCoursesData,
      headerTitle,
      headerIcon,
      startText,
      isPreview,
    )
  }

  lastRequestId = requestId

  let badges = []
  let rewardData = null
  let courses = []
  try {
    ;[badges, rewardData, courses] = await Promise.all([
      fetchBadges(collectionId),
      fetchBadgeReward(collectionId),
      fetchCourses(collectionId),
    ])
    lastFetchTime = Date.now()
  } catch (error) {
    console.error('Failed to fetch badge data:', error)
  }

  // Reset reward to defaults ONLY when adding a new badge list to the editor
  if (resetReward) {
    rewardData = {
      rewardHtml: defaultRewardCardHtml(),
      isEnabled: true,
    }
    updateBadgeReward(collectionId, defaultRewardCardHtml(), true)
  } else if (!rewardData || !rewardData.rewardHtml) {
    // API returned no data, use cached data if available
    rewardData = (window.badgeRewardData && window.badgeRewardData.rewardHtml)
      ? window.badgeRewardData
      : { rewardHtml: defaultRewardCardHtml(), isEnabled: true }
  }

  window.badgesData = badges
  window.badgeRewardData = rewardData
  window.badgeCoursesData = courses

  return buildBadgeListHtml(badges, rewardData, rewardBgColor, collectionId, courses, headerTitle, headerIcon, startText, isPreview)
}

const buildBadgeListHtml = (badges, rewardData, rewardBgColor, collectionId, courses = [], headerTitle = '', headerIcon = '', startText = 'Start', isPreview = false) => {
  const displayTitle = headerTitle || 'Path to Success'
  const displayIcon = headerIcon || 'mingcute:star-fill'

  // No courses at all
  if (courses.length === 0) {
    if (isPreview) {
      return `
        <div class="badge-list-wrapper">
          <div class="badge-list-header">
            <div class="badge-list-header-left">
              <div class="badge-list-header-icon" data-badge-icon="${displayIcon}">
                <iconify-icon icon="${displayIcon}" width="20" height="20" style="color:#015FFB;"></iconify-icon>
              </div>
              <span class="badge-list-header-title">${displayTitle}</span>
            </div>
            <div class="badge-list-header-right">
              <div class="badge-progress-bar"></div>
              <span class="badge-list-progress-count">0/0</span>
            </div>
          </div>
          <div class="badge-list-header-divider"></div>
          <div class="badge-list-empty-state">
            <div class="badge-list-empty-icon" style="border-radius:18px;background:#E3F3FF;">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M4.5 28.5001C8.67691 26.0885 13.8231 26.0885 18 28.5001C22.1769 26.0885 27.3231 26.0885 31.5 28.5001" stroke="#015FFB" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4.5 9.00006C8.67691 6.58852 13.8231 6.58852 18 9.00006C22.1769 6.58852 27.3231 6.58852 31.5 9.00006" stroke="#015FFB" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4.5 9V28.5" stroke="#015FFB" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18 9V28.5" stroke="#015FFB" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M31.5 9V28.5" stroke="#015FFB" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="badge-list-empty-title">No courses available yet</div>
            <div class="badge-list-empty-desc">There are no courses available at the moment.<br/>Once new courses are published, they will appear here.</div>
          </div>
        </div>
      `
    }
    return `
      <div class="badge-list-wrapper">
        <div class="badge-list-header">
          <div class="badge-list-header-left">
            <div class="badge-list-header-icon" data-badge-icon="${displayIcon}" title="Click to change icon">
              <iconify-icon icon="${displayIcon}" width="20" height="20" style="color:#015FFB;"></iconify-icon>
            </div>
            <span class="badge-list-header-title" contenteditable="true" data-cke-ignore-events="true">${displayTitle}</span>
          </div>
          <div class="badge-list-header-right">
            <div class="badge-progress-bar"></div>
            <span class="badge-list-progress-count">0/0</span>
          </div>
        </div>
        <div class="badge-list-header-divider"></div>
      </div>
    `
  }

  const hasBadges = badges.length > 0

  // Courses exist but no badges
  if (!hasBadges) {
    if (isPreview) {
      return `
        <div class="badge-list-wrapper">
          <div class="badge-list-header">
            <div class="badge-list-header-left">
              <div class="badge-list-header-icon" data-badge-icon="${displayIcon}">
                <iconify-icon icon="${displayIcon}" width="20" height="20" style="color:#015FFB;"></iconify-icon>
              </div>
              <span class="badge-list-header-title">${displayTitle}</span>
            </div>
            <div class="badge-list-header-right">
              <div class="badge-progress-bar"></div>
              <span class="badge-list-progress-count">0/0</span>
            </div>
          </div>
          <div class="badge-list-header-divider"></div>
          <div class="badge-list-empty-state">
            <div class="badge-list-empty-icon" style="border-radius:20px;background:#F6F2FF;">
              <iconify-icon icon="mingcute:star-fill" width="36" height="36" style="color:#7C3AED;"></iconify-icon>
            </div>
            <div class="badge-list-empty-title">No badges available yet</div>
            <div class="badge-list-empty-desc">Badges haven't been enabled for courses yet.<br/>Once badges are available, you'll see your achievements here.</div>
          </div>
        </div>
      `
    }
    const courseLinks = courses.map(c =>
      `<a href="/mini-course/${c.id}/completion" class="badge-list-no-badge-link" target="_blank" rel="noopener noreferrer">${c.name}</a>`
    )
    let courseNamesHtml
    if (courseLinks.length === 1) {
      courseNamesHtml = courseLinks[0]
    } else if (courseLinks.length === 2) {
      courseNamesHtml = `${courseLinks[0]} and ${courseLinks[1]}`
    } else {
      courseNamesHtml = `${courseLinks.slice(0, -1).join(', ')} and ${courseLinks[courseLinks.length - 1]}`
    }
    const isSingular = courses.length === 1
    const verb = isSingular ? "isn't" : "aren't"
    const pronoun = isSingular ? "it doesn't have badge" : "they don't have badges"
    const clickText = isSingular ? "Click the course name to add a badge." : "Click a course name to add a badge."
    return `
      <div class="badge-list-wrapper">
        <div class="badge-list-header">
          <div class="badge-list-header-left">
            <div class="badge-list-header-icon" data-badge-icon="${displayIcon}" title="Click to change icon">
              <iconify-icon icon="${displayIcon}" width="20" height="20" style="color:#015FFB;"></iconify-icon>
            </div>
            <span class="badge-list-header-title" contenteditable="true" data-cke-ignore-events="true">${displayTitle}</span>
          </div>
        </div>
        <div class="badge-list-header-divider"></div>
        <div class="badge-list-no-badge-courses">
          <span class="badge-list-no-badge-names">${courseNamesHtml}</span>
          <span class="badge-list-no-badge-label"> ${verb} displayed here because ${pronoun} assigned. ${clickText}</span>
        </div>
      </div>
    `
  }

  const rewardHtml = renderCompletionReward(rewardData, rewardBgColor, collectionId, isPreview)

  // Order badge cards by their course's position in the collection so the list
  // follows the course order regardless of the order the API returns badges in.
  const courseOrderById = new Map(
    courses.map(c => [c.id, c.courseCollectionOrder ?? 0]),
  )
  const orderedBadges = [...badges].sort(
    (a, b) =>
      (courseOrderById.get(a.courseId) ?? 0) -
      (courseOrderById.get(b.courseId) ?? 0),
  )

  const badgeCardsHtml = orderedBadges.map(badge => renderBadgeCard(badge, startText, isPreview)).join('')
  const totalBadges = orderedBadges.length

  // Find courses without badges
  const badgeCourseIds = new Set(badges.map(b => b.courseId))
  const coursesWithoutBadges = courses.filter(c => !badgeCourseIds.has(c.id))
  const noBadgeHtml = !isPreview && coursesWithoutBadges.length > 0
    ? (() => {
      const courseLinks = coursesWithoutBadges.map(c =>
        `<a href="/mini-course/${c.id}/completion" class="badge-list-no-badge-link" target="_blank" rel="noopener noreferrer">${c.name}</a>`
      )
      let courseNamesHtml
      if (courseLinks.length === 1) {
        courseNamesHtml = courseLinks[0]
      } else if (courseLinks.length === 2) {
        courseNamesHtml = `${courseLinks[0]} and ${courseLinks[1]}`
      } else {
        courseNamesHtml = `${courseLinks.slice(0, -1).join(', ')} and ${courseLinks[courseLinks.length - 1]}`
      }
      const isSingular = coursesWithoutBadges.length === 1
      const verb = isSingular ? "isn't" : "aren't"
      const pronoun = isSingular ? "it doesn't have badge" : "they don't have badges"
      const clickText = isSingular ? "Click the course name to add a badge." : "Click a course name to add a badge."
      return `<div class="badge-list-no-badge-courses">
        <span class="badge-list-no-badge-names">${courseNamesHtml}</span>
        <span class="badge-list-no-badge-label"> ${verb} displayed here because ${pronoun} assigned. ${clickText}</span>
      </div>`
    })()
    : ''

  // Progress bar segments based on badges API
  const segmentTotal = totalBadges
  const progressSegments = Array.from({ length: segmentTotal }, () =>
    `<div class="badge-progress-segment"></div>`
  ).join('')

  return `
    <div class="badge-list-wrapper">
      <div class="badge-list-header">
        <div class="badge-list-header-left">
          <div class="badge-list-header-icon" data-badge-icon="${displayIcon}" title="Click to change icon">
            <iconify-icon icon="${displayIcon}" width="20" height="20" style="color:#015FFB;"></iconify-icon>
          </div>
          <span class="badge-list-header-title" contenteditable="true" data-cke-ignore-events="true">${displayTitle}</span>
        </div>
        <div class="badge-list-header-right">
          <div class="badge-progress-bar">
            ${progressSegments}
          </div>
          <span class="badge-list-progress-count">0/${segmentTotal}</span>
        </div>
      </div>
      <div class="badge-list-header-divider"></div>
      ${rewardHtml}
      <div class="badge-list-cards">
        ${badgeCardsHtml}
      </div>
      ${noBadgeHtml}
    </div>
  `
}

const initHeaderEvents = (container, editor, modelElement) => {
  if (!container || !editor || !modelElement) return

  const dispatch = window.store?.dispatch
  const titleEl = container.querySelector('.badge-list-header-title')
  const iconEl = container.querySelector('.badge-list-header-icon')

  if (titleEl) {
    titleEl.addEventListener('blur', () => {
      const newTitle = titleEl.textContent.trim()
      editor.model.change(writer => {
        writer.setAttribute('badgeHeaderTitle', newTitle, modelElement)
      })
    })
  }

  if (iconEl && dispatch) {
    iconEl.style.cursor = 'pointer'
    iconEl.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()

      const handleIconSelect = icon => {
        const iconifyEl = iconEl.querySelector('iconify-icon')
        if (iconifyEl) {
          iconifyEl.setAttribute('icon', icon)
        }
        iconEl.setAttribute('data-badge-icon', icon)
        editor.model.change(writer => {
          writer.setAttribute('badgeHeaderIcon', icon, modelElement)
        })
        dispatch(messageBoxHideAction('message-box-1'))
      }

      const handleClickOutside = event => {
        const messageBox = document.getElementById('message-box-1')
        if (messageBox && !messageBox.contains(event.target)) {
          document.removeEventListener('mousedown', handleClickOutside)
          dispatch(messageBoxHideAction('message-box-1'))
        }
      }
      document.addEventListener('mousedown', handleClickOutside)

      dispatch(
        messageBoxShowAction(
          MessageBoxContent({
            type: MessageBoxType.WARNING,
            title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <typography>Select Icon</typography>
            </div>`,
            buttons: [],
            component: (
              <SelectedIcon
                editor={editor}
                onIconSelect={handleIconSelect}
              />
            ),
            variant: MessageBoxVariant.DRAGGABLE,
            callback: () => { },
          }),
        ),
      )
    })
  }
}

export { initRewardEvents, updateBadgeReward, initHeaderEvents }
export default CreateBadgeList
